import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Eye, HeartHandshake, Lightbulb, ShieldCheck, Target, Users } from "lucide-react";

const photos = [
  { src: "https://edu.kiantechnologies.in/assets/img/about/g16.jpeg", alt: "Kian Technologies cybersecurity workshop lab" },
  { src: "https://edu.kiantechnologies.in/assets/img/about/g14.jpeg", alt: "Kian Technologies practical training session" },
  { src: "https://edu.kiantechnologies.in/assets/img/about/g15.jpeg", alt: "Kian Technologies hands-on classroom" },
];

export const AboutPage = () => (
  <main className="min-h-screen bg-background text-foreground">
    <section className="relative overflow-hidden border-b border-border/40 py-16 md:py-24">
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono uppercase tracking-wider">
            Kian Technologies • Cyber Security Education
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Protecting the Future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Digital Assets</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Kian Technologies is a cybersecurity education and training institute focused on practical learning, ethical security skills and real-world mentorship. Our goal is to help the next generation build the knowledge and discipline needed to make the digital world safer.
          </p>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="text-xs font-mono uppercase tracking-wider text-emerald-400">Who We Are</span>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2">Building the Shield for Digital India</h2>
          <div className="mt-5 space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>Kian Technologies is based in Bhilai, Chhattisgarh and focuses on cybersecurity education across ethical hacking, penetration testing, network security and security operations.</p>
            <p>Our training philosophy bridges academic concepts and industry expectations through practical labs, guided exercises, projects and mentorship.</p>
            <p>We also support cybersecurity clubs, workshops, CTF activities and awareness programs that encourage collaboration, leadership and responsible security practice.</p>
          </div>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {["Hands-on practical learning", "Industry-oriented curriculum", "Mentorship and guidance", "Ethical security mindset"].map((item) => (
              <div key={item} className="flex items-start gap-2 p-3 rounded-lg border border-border bg-card">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-xs font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <img src={photos[0].src} alt={photos[0].alt} className="w-full h-64 object-cover rounded-xl border border-border shadow-lg col-span-2" loading="lazy" />
          <img src={photos[1].src} alt={photos[1].alt} className="w-full h-40 object-cover rounded-xl border border-border" loading="lazy" />
          <img src={photos[2].src} alt={photos[2].alt} className="w-full h-40 object-cover rounded-xl border border-border" loading="lazy" />
        </div>
      </div>
    </section>

    <section className="py-16 bg-muted/20 border-y border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
        <div className="p-7 rounded-2xl bg-card border border-border">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5"><Target className="w-5 h-5 text-emerald-400" /></div>
          <h2 className="text-xl font-bold">Our Mission</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">To empower the next generation of IT and cybersecurity professionals through hands-on training, practical exposure and real-world learning experiences.</p>
        </div>
        <div className="p-7 rounded-2xl bg-card border border-border">
          <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5"><Eye className="w-5 h-5 text-cyan-400" /></div>
          <h2 className="text-xl font-bold">Our Vision</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">To create a safer and more efficient digital world by developing skilled, responsible experts who can adapt to evolving technology and security challenges.</p>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-mono uppercase tracking-wider text-emerald-400">Our Core Values</span>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2">Learn responsibly. Build practically. Secure ethically.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            [ShieldCheck, "Integrity First", "Trust, responsibility and ethical conduct come before technical ability."],
            [Lightbulb, "Continuous Innovation", "Security changes every day, so learning and research must keep moving."],
            [Users, "Community Support", "We encourage peer learning, clubs, workshops and collaborative problem solving."],
            [HeartHandshake, "Practical Impact", "We focus on skills that learners can apply in labs, projects and real work environments."],
          ].map(([Icon, title, desc]) => (
            <div key={title} className="p-6 rounded-xl bg-card border border-border">
              <Icon className="w-5 h-5 text-emerald-400" />
              <h3 className="mt-4 font-semibold">{title}</h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 border-t border-border/40 text-center bg-gradient-to-t from-emerald-950/20 to-transparent">
      <div className="max-w-2xl mx-auto px-4">
        <h2 className="text-2xl font-bold">Explore the 15-Day Masterclass</h2>
        <p className="mt-2 text-sm text-muted-foreground">A practical cybersecurity learning experience built for students and beginners.</p>
        <Link to="/register" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-sm transition-colors">Apply for the Masterclass <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </section>
  </main>
);
